import React from 'react';
import { createRoot } from 'react-dom/client';

import { AuthSharedLanding } from '@cheerfy/lib/reactive/pages/landing/SharedLanding';
import { ActionButtonsType } from '@cheerfy/lib/reactive/pages/landing/shared/actions.enum';
import { ShopSessionAPI } from '@cheerfy/lib/js/shop-session/index';
import { LandingButtonCtx } from '@cheerfy/lib/reactive/features/landing/components/LandingButton';

import { UrlsAPI } from '@cheerfy/lib/js/urls';
import { GoogleAnalytics } from '@cheerfy/lib/js/analytics/google';
import { Channel } from '@cheerfy/lib/common/shared/channels';

import { Page } from './page';
import { MenuComponent } from '../components/cheerfypay/menu/menu.component';
import {
    getCommonSharedLandingProps, getEnvFromForm, getLang, getScopeFromLocation, mapPayActionButtons,
} from '../utils/landing';

import { URL as URLModel } from '../integrations/url/url';
import { OrderNowModal } from '../modals/order-now.modal';
import { Locale } from '../integrations/locale/locale';
import { getTableUrl } from '../utils/table';
import { PayLandingResponse } from '../shared/types/backend/pay-response.type';
import { Authentication } from '../integrations/authentication/authentication';
import { ServicesRepository } from '../repositories/services/servicesRepository';
import { SimpleModal } from '../modals/simple-modal.modal';
import { Status } from '../shared/enums/status.enum';
import { ServiceType } from '../shared/enums/service-type.enum';

export class CheerfyPayMenuPage extends Page {
    private readonly _menuComponent: MenuComponent;
    private readonly _orderNowModal: OrderNowModal;

    private readonly _servicesRepository: ServicesRepository;
    private readonly _noServicesModal: SimpleModal;

    public constructor() {
        super();

        this._menuComponent = new MenuComponent($('.menu'));
        this._orderNowModal = new OrderNowModal($('.order-now'));

        this._orderNowModal.on('OnModalContinue', this.onModalContinue.bind(this));

        this._servicesRepository = new ServicesRepository();
        this._noServicesModal = SimpleModal.create({
            title: $.i18n('no_services_available_title'),
            content: $.i18n('no_services_available_content'),
            unCancellable: true,
            dismissible: true,
        });

        this._checkUrlParams();
    }

    private _checkUrlParams() {
        const params = URLModel.getQueryParams<{
            popup: string
        }>();

        if (params?.popup) {
            this._menuComponent.open();
        }
    }

    private async _onOrderLinkClick(context: any, landingCtx: LandingButtonCtx) {
        const [locationId, tableNumber] = URLModel.getUrlData();
        const content = context.content as any;

        if (content === undefined) {
            return;
        }

        const services = await this._servicesRepository.getServices(Number(locationId));
        const hasAvailableService = services.some((
            { status, serviceType },
        ) => status === Status.ENABLED && serviceType === ServiceType.DINEIN);

        if (!hasAvailableService) {
            this._noServicesModal.open();
            return;
        }

        const link = content[Locale.getNavigatorLanguage()] ?? content.default;
        let url = new URL(await GoogleAnalytics.appendAnalyticsToUrl(link));

        if (landingCtx.customer) {
            url = new URL(UrlsAPI.replaceParameters(url, {
                'external_user_id': landingCtx.customer.profile.externalId,
            }));
        }

        const payResponse = Page.getPayResponseOrFail<PayLandingResponse>();
        const { dinners = false } = payResponse?.pay_configuration?.settings?.configuration?.options ?? {};

        if (!tableNumber || (tableNumber && dinners)) {
            this._orderNowModal.open(url);

            return;
        }

        this._redirectToShop(tableNumber, url);
    }

    private onModalContinue(data: any) {
        const [tableNumber, link, dinnersNumber] = data;

        this._redirectToShop(tableNumber, link, dinnersNumber);
    }

    private async _redirectToShop(tableNumber: string, url: URL, dinnersNumber?: number) {
        const replacedUrlStr = getTableUrl(tableNumber, url);
        const replacedUrl = new URL(replacedUrlStr);

        const { backendBaseUrl } = getEnvFromForm();

        const sessionData = this._getSessionDataRequest(tableNumber, dinnersNumber);
        const res = await ShopSessionAPI.createShopSession(
            backendBaseUrl,
            sessionData,
            Authentication.getInstance().getCurrentToken()
        );

        if (res?.id) {
            window.location.href = UrlsAPI.replaceParameters(replacedUrl, {
                'shopSession': res.id,
            });

            return;
        }

        window.location.href = replacedUrl.toString();
    }

    private _getSessionDataRequest(
        tableNum: string | null,
        dinnersNumber?: number,
    ): ShopSessionAPI.SaveShopServiceSessionRequest {
        const [locationId, tableNumber] = URLModel.getUrlData();

        const data: ShopSessionAPI.SaveShopServiceSessionRequest = {
            addressExtra: '',
            restaurant: {
                id: parseInt(locationId, 10),
            },
            service: {
                type: 'dinein',
                now: true,
                channel: Channel.SHOP,
                source: Channel.PAY,
            },
            dineIn: {
                tableId: tableNum || tableNumber,
            },
        };

        if (dinnersNumber) {
            data.service.numberPeople = dinnersNumber;
        }

        return data;
    }

    public async render(): Promise<Page> {
        const [locationId] = URLModel.getUrlData();
        const parsedLocationId = Number(locationId);
        const payResponse = Page.getPayResponseOrFail<PayLandingResponse>();

        const contentOverride = {
            [ActionButtonsType.ORDER_LINK]:
                (ctx: any) => (landingCtx: LandingButtonCtx) => this._onOrderLinkClick(ctx, landingCtx),
            [ActionButtonsType.PAY_LINK]: (ctx: any) => () => this._menuComponent.open(),
        };

        const sharedProps = getCommonSharedLandingProps(
            payResponse.pay_configuration,
            parsedLocationId,
            contentOverride,
        );

        const lang = getLang(payResponse.pay_configuration?.settings?.configuration?.supported_languages ?? []);

        const guestProps = {
            ...sharedProps,
            rotative: {
                titles: payResponse?.pay_configuration?.settings?.guest?.text?.[lang]?.titles ?? [],
                subtitles: payResponse?.pay_configuration?.settings?.guest?.text?.[lang]?.subtitles ?? [],
            },
            buttons: mapPayActionButtons(
                payResponse?.pay_configuration?.settings?.guest?.actions ?? [],
                lang,
                contentOverride,
            ),
        };

        const authProps = {
            ...sharedProps,
            rotative: {
                titles: payResponse?.pay_configuration?.settings?.user?.text?.[lang]?.titles ?? [],
                subtitles: payResponse?.pay_configuration?.settings?.user?.text?.[lang]?.subtitles ?? [],
            },
            buttons: mapPayActionButtons(
                payResponse?.pay_configuration?.settings?.user?.actions ?? [],
                lang,
                contentOverride,
            ),
        };

        const authentication = Authentication.getInstance();

        const element = React.createElement(AuthSharedLanding, {
            env: getEnvFromForm(),
            scope: getScopeFromLocation(parsedLocationId),
            authScope: getScopeFromLocation(parsedLocationId),
            guest: guestProps,
            authenticated: authProps,
            portalId: payResponse.pay_configuration.portal_id,
            onAuthChange: authentication.handleCustomerAuth.bind(authentication),
        });

        const root = createRoot(<Element>document.getElementById('landing-mnt'));

        root.render(element);

        return super.render();
    }
}
